import React, { useState, useEffect } from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Slider,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem
} from "@material-tailwind/react";
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';
import {Spinner} from '@material-tailwind/react'
import { debounce } from 'lodash';
import toast, { Toaster } from 'react-hot-toast';
import { getStampAssetDetail, getStampAssetHolders } from '../../util/stamp-api';
import { formatInscriptionId, formatAddress } from '../../util/format-data';
import StampImageCard from '../../components/TokenSaleCard/StampImageCard';
import stampImg from '../../assets/imgs/stamps-LKW6KikM.svg'
import { ArrowRightIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { getMarketplaceData } from '../../util/api';
import StampCard from '../../components/Cards/StampCard';

export default function StampAssetDetail() {
  const { param } = useParams();
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const [assetDetail, setAssetDetail] = useState({});
  const [assetHolders, setAssetHolders] = useState([]);
  const [holdersTotal, setHoldersTotal] = useState([]);
  const [holdersPage, setHoldersPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [holdersLimit, setHoldersLimit] = useState(6);
  const [sales, setSales] = useState([]);
  const [salesTotal, setSalesTotal] = useState([]);
  const [salesPage, setSalesPage] = useState(1);
  const [salesLimit, setSalesLimit] = useState(4);
  const [marketTotal, setMarketTotal] = useState(0);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    fetchData();
  }, [])

  useEffect(() => {
    fetchHolders();
  }, [holdersPage])

  useEffect(() => {
    fetchSales();
  }, [salesPage, refresh])

  const fetchHolders = async () => {
    setLoading(true);
    let response = await getStampAssetHolders(param, holdersLimit, holdersPage);
    if (response.data) {
      setAssetHolders(response.data);
      setTotal(response.total);
      setHoldersTotal(response.totalPages);
    }
    else {
      setAssetHolders([]);
      setTotal(0);
      setHoldersTotal(0);
    }
    setLoading(false);
  }

  const fetchSales = async () => {
    setLoading(true);
    let data = 
      {
        ticker: param,
        deleteFlag: 0,
        type: 7,
        reason: "sale",
        orderBy: "id",
        orderDir: "desc",
        offset: salesPage,
        limit: salesLimit
      }
    let response = await getMarketplaceData(data);
    setSales(response.data);
    setMarketTotal(response.total);
    setSalesTotal(Math.ceil(response.total / salesLimit));
    setLoading(false);
  }

  const fetchData = async () => {
    setLoading(true);
    let response = await getStampAssetDetail(param);
    if (response.data.stamp) {
      setAssetDetail(response.data.stamp);
    }
    else {
      setAssetDetail({});
    }
    setLoading(false);
  }

  const holdersNext = () => {
    if (holdersPage === holdersTotal) return;
    setHoldersPage(holdersPage + 1);
  };
 
  const holdersPrev = () => {
    if (holdersPage === 1) return;
    setHoldersPage(holdersPage - 1);
  };

  const salesNext = () => {
    if (salesPage === salesTotal) return;
    setSalesPage(salesPage + 1);
  };
 
  const salesPrev = () => {
    if (salesPage === 1) return;
    setSalesPage(salesPage - 1);
  };

  const handleCopy = (value) => {
    navigator.clipboard.writeText(value).then(
      () => {
        toast.success(t('Copied!'));
      },
      (err) => {
        // Failed to copy to clipboard
        console.error('Could not copy address: ', err);
      }
    );
  }

  const handleActivity = () => {

  }
  return (
    <div className="text-white mt-[65px] w-full max-w-[1500px] md:px-7 px-2 flex flex-col justify-center items-center mb-10 min-h-[600px]">
      {
        loading ? <Spinner className="h-16 w-16 text-gray-900/50 fixed" color="pink"/> : <></>
      }
      <div className="flex flex-row w-full items-center font-bold text-primary sm:text-[32px] text-[24px] mt-2">
        <img src={stampImg} className="sm:w-[36px] w-[28px]" alt=""/>
        <span>{param}</span>
      </div>
      <div className="grid md:grid-cols-2 grid-cols-1 gap-3 w-full">
        <div className="flex flex-col gap-3 border-solid border-[2px] rounded-lg border-gray-800 p-3">
          <div className="flex flex-row gap-1 items-center">
            <InformationCircleIcon strokeWidth={2} className="text-primary h-6 w-6" />
            <div className="text-primary">{t('Asset Information')}</div>
          </div>
          <div className="flex flex-row p-2 border-b-[1px] border-b-solid border-gray-700">
            <div className="sm:w-[35%] w-[50%]">{t('Stamp Number')}</div>
            <div className="sm:w-[65%] w-[50%]">{assetDetail.stamp}</div>
          </div>
          <div className="flex flex-row p-2 border-b-[1px] border-b-solid border-gray-700">
            <div className="sm:w-[35%] w-[50%]">{t('Creator')}</div>
            <div className="sm:w-[65%] w-[50%] cursor-pointer sm:flex hidden" onClick={() => {handleCopy(assetDetail.creator)}}>{formatInscriptionId(assetDetail.creator)}</div>
            <div className="sm:w-[65%] w-[50%] cursor-pointer sm:hidden flex" onClick={() => {handleCopy(assetDetail.creator)}}>{formatAddress(assetDetail.creator)}</div>
          </div>
          <div className="flex flex-row p-2 border-b-[1px] border-b-solid border-gray-700">
            <div className="sm:w-[35%] w-[50%]">{t('Supply')}</div>
            <div className="sm:w-[65%] w-[50%]">{assetDetail.supply}</div>
          </div>
          <div className="flex flex-row p-2 border-b-[1px] border-b-solid border-gray-700">
            <div className="sm:w-[35%] w-[50%]">{t('Divisible')}</div>
            <div className="sm:w-[65%] w-[50%]">{assetDetail.divisible ? "YES" : "NO"}</div>
          </div>
          <div className="flex flex-row p-2 border-b-[1px] border-b-solid border-gray-700">
            <div className="sm:w-[35%] w-[50%]">{t('Locked')}</div>
            <div className="sm:w-[65%] w-[50%]">{assetDetail.locked ? "YES" : "NO"}</div>
          </div>
          <div className="flex flex-row p-2 border-b-[1px] border-b-solid border-gray-700">
            <div className="sm:w-[35%] w-[45%]">{t('Block')}</div>
            <div className="sm:w-[65%] w-[50%]">{assetDetail.block_index}</div>
          </div>
          <div className="flex flex-row p-2 border-b-[1px] border-b-solid border-gray-700">
            <div className="sm:w-[35%] w-[45%]">{t('Transaction')}</div>
            <div className="sm:w-[65%] w-[50%] cursor-pointer sm:flex hidden" onClick={() => {handleCopy(assetDetail.tx_hash)}}>{formatInscriptionId(assetDetail.tx_hash)}</div>
            <div className="sm:w-[65%] w-[50%] cursor-pointer sm:hidden flex" onClick={() => {handleCopy(assetDetail.tx_hash)}}>{formatAddress(assetDetail.tx_hash)}</div>
          </div>
        </div>
        <div className="flex flex-col gap-3 border-solid border-[2px] rounded-lg border-gray-800 p-3 items-center">
          {
            assetDetail.stamp_url ? 
            <StampImageCard data= {assetDetail} />
            :<></>
          }
        </div>
      </div>
      <div className="flex flex-col mt-3 w-full gap-3 border-solid border-[2px] rounded-lg border-gray-800 p-3 items-center">
        <div className="w-full text-primary font-bold text-[20px]">{t('Holders')}({total})</div>
        <div className="w-full gap-3 max-w-[800px]">
          <div className="flex flex-col gap-3">
            <div className="flex flex-row border-b-[1px] border-solid border-gray-700 p-2">
              <div className="sm:w-[50%] w-[60%]">{t('Address')}</div>
              <div className="sm:w-[20%] w-[40%]">{t('Amount')}</div>
              <div className="sm:w-[30%] sm:flex hidden">{t('Percent')}</div>
            </div>
            <>
              {
                assetHolders.map((data) => {
                  return (
                    <div className="flex flex-row border-b-[1px] border-solid border-gray-700 p-2" key = {data.address}>
                      <div className="sm:w-[50%] sm:flex hidden cursor-pointer" onClick={() => {handleCopy(data.address)}}>{formatInscriptionId(data.address)}</div>
                      <div className="sm:hidden w-[60%] cursor-pointer" onClick={() => {handleCopy(data.address)}}>{formatAddress(data.address)}</div>
                      <div className="sm:w-[20%] w-[40%]">{data.amt}</div>
                      <div className="sm:w-[30%] sm:flex hidden">{data.percentage} %</div>
                    </div>
                  )
                })
              }
            </>
          </div>

        </div>
        {
          holdersTotal > 1 ? 
          <div className="flex flex-row items-center gap-8 justify-end mt-5">
            <IconButton
              size="sm"
              variant="outlined"
              onClick={holdersPrev}
              disabled={holdersPage === 1}
              className="bg-primary hover:bg-primary-hover"
            >
              <ArrowLeftIcon strokeWidth={2} className="h-4 w-4" />
            </IconButton>
            <Typography color="white" className="font-normal">
              {t('Page')} <strong className="text-primary">{holdersPage}</strong> of{" "}
              <strong className="text-primary">{holdersTotal}</strong>
            </Typography>
            <IconButton
              size="sm"
              variant="outlined"
              onClick={holdersNext}
              disabled={holdersPage === holdersTotal}
              className="bg-primary hover:bg-primary-hover"
            >
              <ArrowRightIcon strokeWidth={2} className="h-4 w-4" />
            </IconButton>
          </div>
          :
          <></>
        }
      </div>
      <div className="flex flex-col mt-3 w-full gap-3 border-solid border-[2px] rounded-lg border-gray-800 p-3 items-center">
        <div className="w-full text-primary font-bold text-[20px]">{t('Listings')}({marketTotal})</div>
        <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-3 w-full mt-3">
          {
            sales != undefined ?
            sales.map((data) => 
              <StampCard key={uuidv4()} data = {data} handleRefresh = {() => setRefresh(1 - refresh)} handleActivity = {handleActivity} type = {7}/>
            ) : <></>
          }
        </div>
        {
          salesTotal > 1 ? 
          <div className="flex flex-row items-center gap-8 justify-end mt-5">
            <IconButton
              size="sm"
              variant="outlined"
              onClick={salesPrev}
              disabled={salesPage === 1}
              className="bg-primary hover:bg-primary-hover"
            >
              <ArrowLeftIcon strokeWidth={2} className="h-4 w-4" />
            </IconButton>
            <Typography color="white" className="font-normal">
              {t('Page')} <strong className="text-primary">{salesPage}</strong> of{" "}
              <strong className="text-primary">{salesTotal}</strong>
            </Typography>
            <IconButton
              size="sm"
              variant="outlined"
              onClick={salesNext}
              disabled={salesPage === salesTotal}
              className="bg-primary hover:bg-primary-hover"
            >
              <ArrowRightIcon strokeWidth={2} className="h-4 w-4" />
            </IconButton>
          </div>
          :
          <></>
        }
      </div>
    </div>
  )
}
